import type { Category, CategoryTree } from "~/types";

export const buildCategoryTree = (categories : Category[], parentId: number | null = null): CategoryTree => {
    return categories
    .filter((cat : Category) => cat.parent === parentId)
    .map((cat : Category) => ({
        ...cat,
        children: buildCategoryTree(categories, cat.id)
    }));
};

export const buildCategoryPath = (categoryId: number | null, categories: Category[] = []) : Category[] => {
    const path: Category[] = [];
    let currentId = categoryId;

    while (currentId !== null) {
        const category = categories.find(cat => cat.id === currentId);
        if (category) {
            path.unshift(category);
            currentId = category.parent;
        } else {
            break;
        }
    }

    return path;
}


export const getOptimalProductCategoryPath = (productCategories: Category[]) : Category[] => {
    if (productCategories.length === 0) {
        return [];
    }

    if (productCategories.length === 1) {
        return productCategories;
    }

    const categoryMap = new Map(productCategories.map(cat => [cat.id, cat]));
    
    // Find leaf categories (categories that are not a parent of any other category)
    const parentIds = new Set(productCategories.map(cat => cat.parent).filter(id => id !== null));
    const leafCategories = productCategories.filter(cat => !parentIds.has(cat.id));

    // Build path for each leaf category going up to root
    const paths: Category[][] = leafCategories.map(leaf => {
        const path: Category[] = [];
        let current: Category | undefined = leaf;
        
        while (current) {
            path.unshift(current);
            current = current.parent !== null ? categoryMap.get(current.parent) : undefined;
        }
        
        return path;
    });

    if (paths.length === 0) {
        return [];
    }

    // Find the longest path
    const maxLength = Math.max(...paths.map(path => path.length));
    const longestPaths = paths.filter(path => path.length === maxLength);

    // Return the first longest path
    return longestPaths[0] ?? [];
}

